import React, { useCallback, useContext, useEffect, useState } from 'react';
import BaseDialog from './BaseDialog';
import { CONTRACT_TYPES } from '../../Views/Contracts/contract_enums';
import { Grid, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import BaseButton from '../Buttons/BaseButton';
import moment from 'moment-timezone';
import { SlideUpTransition } from '../Transitions/Transitions';
import { GetFeesAPI } from '../../API/Fees/FeesAPI';
import { UserContext } from '../../Contexts/UserContext';
import { AlertsDispatchContext } from '../../Contexts/AlertsContext';
import { extractErrorMessage } from '../../Utils/Errors/Errors';
import {
  IndustrialConceptText,
  IndustrialFeeConcept
} from '../../Enums/concepts';
import { moneyWithDots } from '../../Utils/Format/MoneyFormat';
import { numberFormat } from '../../Utils/Format/Number';
import Skeleton from '@material-ui/lab/Skeleton';
import ErrorState from '../EmptyState/ErrorState';
import { FeeLink } from '../../Configs/Links';

const renderExternalResource = () => (
  <>
    Para mayor información, puede consultar la{' '}
    <a href={FeeLink} target="_blank" rel="help noopener noreferrer">
      página principal.
    </a>
  </>
);

const getRangeString = (min, max) => {
  //The current máximum range is 999.999.999
  if (max > 999e6) {
    return `De ${numberFormat(min)} m³ en adelante`;
  }

  return `De ${numberFormat(min)} m³ a ${numberFormat(max)} m³`;
};

const LoadingState = () => (
  <Grid container>
    <Grid item xs={6}>
      <Skeleton variant="text" />
    </Grid>
    <Grid item xs={12}>
      <Grid container justifyContent="space-between">
        <Grid item xs={4}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={2}>
          <Skeleton variant="text" />
        </Grid>
      </Grid>
    </Grid>
    <Grid item xs={12}>
      <Grid container justifyContent="space-between">
        <Grid item xs={4}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={2}>
          <Skeleton variant="text" />
        </Grid>
      </Grid>
    </Grid>
    <Grid item xs={6}>
      <Skeleton variant="text" />
    </Grid>
    <Grid item xs={12}>
      <Grid container justifyContent="space-between">
        <Grid item xs={4}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={2}>
          <Skeleton variant="text" />
        </Grid>
      </Grid>
    </Grid>
    <Grid item xs={12}>
      <Grid container justifyContent="space-between">
        <Grid item xs={4}>
          <Skeleton variant="text" />
        </Grid>
        <Grid item xs={2}>
          <Skeleton variant="text" />
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);

const FeesDialog = ({
  open = true,
  onClose,
  type = CONTRACT_TYPES.Industrial,
  contractId
}) => {
  const classes = useStyles();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentUser = useContext(UserContext);
  const setAlert = useContext(AlertsDispatchContext);
  const isMobileSize = useMediaQuery(theme => theme.breakpoints.down('xs'));

  useEffect(() => {
    if (type !== CONTRACT_TYPES.Industrial) {
      onClose();
      return;
    }

    let ignoreRequest = false;
    const fetchData = async () => {
      setLoading(true);
      const response = await GetFeesAPI(contractId, currentUser.token);

      setLoading(false);
      if (!response.success) {
        const error = extractErrorMessage(response);
        setAlert({
          type: 'error',
          message: error.message
        });
        return;
      }
      if (ignoreRequest) {
        return;
      }
      setFees(response.data);
    };

    fetchData();
    return () => {
      ignoreRequest = true;
    };
  }, [onClose, type, contractId, currentUser, setAlert]);

  const content = useCallback(() => {
    if (loading) {
      return <LoadingState />;
    }

    if (fees.length === 0) {
      return (
        <ErrorState
          messageTitle="No se encontraron tarifas vigentes."
          subMessage={renderExternalResource()}
        />
      );
    }

    return (
      fees.length > 0 && (
        <Grid container>
          <Grid item xs={12}>
            <Typography className={classes.title}>
              {IndustrialConceptText[IndustrialFeeConcept.consumption]}:
            </Typography>
          </Grid>
          {fees
            .filter(fee => fee.conceptId === IndustrialFeeConcept.consumption)
            .map((fee, index) => (
              <Grid item xs={12} key={index}>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Typography>
                      {getRangeString(fee.minimum, fee.maximum)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>{moneyWithDots(fee.value)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          {fees
            .filter(fee => fee.conceptId !== IndustrialFeeConcept.consumption)
            .map((fee, index) => (
              <Grid item xs={12} key={index}>
                <hr />
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Typography className={classes.title}>
                      {IndustrialConceptText[fee.conceptId]}:
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography>
                      {fee.percentage === 0
                        ? `${moneyWithDots(fee.value)}`
                        : `${numberFormat(fee.percentage)}%`}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          <Grid item xs={12}>
            <hr />
            <Grid container justifyContent="space-between">
              <Grid item>
                <Typography>{renderExternalResource()}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )
    );
  }, [loading, fees, classes]);

  const actions = useCallback(
    () => (
      <Grid container direction="row-reverse">
        <Grid item xs="auto">
          <BaseButton onClick={onClose}>Cerrar</BaseButton>
        </Grid>
      </Grid>
    ),
    [onClose]
  );

  const currentMonthYear = moment()
    .tz('America/Bogota')
    .format('MMMM YYYY');

  return (
    <BaseDialog
      open={open}
      handleClose={onClose}
      title={`Tarifas ${currentMonthYear}`}
      content={content}
      actions={actions}
      fullScreen={isMobileSize}
      contentSize="medium"
      TransitionComponent={isMobileSize ? SlideUpTransition : undefined}
      disableBackdropClick={true}
    />
  );
};

const useStyles = makeStyles(() => ({
  title: {
    fontSize: 14,
    fontWeight: 600
  }
}));
export default FeesDialog;
